import {
  AfterContentChecked,
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { DetailsContainer } from 'src/app/core/containers/details-container';
import { FacilityBuilderService } from 'src/app/services/builder/facility-builder.service';
import { LogAndMessageService } from 'src/app/services/log-and-message.service';
import { isNullOrUndefined } from 'src/app/utils/utils';
import { OpAlertLogTableService } from 'src/app/services/table/op-alert-log-table.service';
import { OperationalMonitoringApiService } from 'src/app/services/api/operational-monitoring-api.service';
import { OpAlertLogsBuilderService } from 'src/app/services/builder/op-alert-logs-builder.service';
import { OpAlertLog } from 'src/app/model/opAlertLogs/op-alert-log';
import { getUserTimezone } from 'src/app/core/functions/date-functions';
import { ProgressSpinnerComponent } from '../../../core/components/progress-spinner.component';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { DatePipe } from '@angular/common';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'app-op-alert-log-print-container',
  templateUrl: './op-alert-log-print-container.component.html',
  styleUrls: ['./op-alert-log-print-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [ProgressSpinnerComponent, ButtonDirective, DatePipe, TranslatePipe],
})
export class OpAlertLogPrintContainerComponent
  extends DetailsContainer
  implements OnInit, OnDestroy, AfterContentChecked
{
  protected _logAndMessage = inject(LogAndMessageService);
  protected _opMonitoringApi = inject(OperationalMonitoringApiService);
  protected _opAlertLogBuilder = inject(OpAlertLogsBuilderService);
  protected _facilityBuilder = inject(FacilityBuilderService);
  protected _opAlertLogTableService = inject(OpAlertLogTableService);

  opAlertLog: OpAlertLog | null = null;
  timezone: string | null = null;

  ngOnInit() {
    this.timezone = getUserTimezone();
    this.setupTabClosingSubscription();
    this.initializeMonLog();
  }

  ngOnDestroy(): void {
    if (this.tabClosingSub) {
      this.tabClosingSub.unsubscribe();
    }
  }

  ngAfterContentChecked(): void {
    const activeId = this.id;
    if (
      !isNullOrUndefined(activeId) &&
      this.opAlertLog &&
      this.opAlertLog.id !== activeId &&
      !this.doCheckComplete
    ) {
      this.doCheckComplete = true;
      this._opAlertLogTableService.setSelected(null);
      this.initializeMonLog();
    }
  }

  initializeMonLog() {
    const id = this.id;

    if (!id) {
      this.opAlertLog = this._opAlertLogTableService.getSelected();
      // no id and nothing cached, go back to search.
      if (!this.opAlertLog) {
        this._logAndMessage.errorLogOnly('Mon log was null');
        const index = this._tabService.getActiveIndex();
        this._tabService.closeTab(index);
      }
      this.initialize();
    } else {
      this._opMonitoringApi.getOpAlertLog(id).subscribe((log) => {
        if (log) {
          this.opAlertLog = log;
          this.doCheckComplete = false;
          this.initialize();
        } else {
          // nothing found, go back to search.
          this._logAndMessage.errorLogOnly(
            'Could not find op alert log with ID: ' + id
          );
          const index = this._tabService.getActiveIndex();
          this._tabService.closeTab(index);
        }
      });
    }
  }

  async initialize() {
    const label = await firstValueFrom(
      this._translateService.get('COMMON.LABEL.PRINT')
    );
    this._tabService.updateActiveTabLabel(
      label + ': ' + this.opAlertLog?.opLogId
    );
  }

  print() {
    // This used to use printjs, but we ran into some issues with lines
    // overlapping each other in the migration to angular 13, so now
    // we use @media print styling to only show what is needed
    // and use the default window.print() to generate the document!
    window.print();
  }
}
